import type { TPropostaData } from '@/features/proposta/interfaces/proposta.type'
import type { TParticipacao, TPropostaSchemaInput, TSimulacaoProposta } from '@/features/proposta/schemas'
import {
  opcaoVagaEnum,
  type TAssinaturaContratoEnum,
  type TFormaPagamentoComissaoEnum,
  type TOpcaoVagaEnum,
} from '@/types/proposta'

class CompradorDtoFactory {
  static toDto(
    compradorCadastrado: TPropostaSchemaInput['cadastroCompradores']['compradores'][0],
    cadastrados: TPropostaSchemaInput['cadastroCompradores'],
    percentualParticipacao?: number,
  ): TPropostaData['compradores'][number] {
    const base = {
      pessoaId: compradorCadastrado.clienteId,
      percentualParticipacao: percentualParticipacao ?? 100,
      tipoCliente: compradorCadastrado.tipo,
    }

    const procuradoresPorCliente = (clienteId: string) => {
      return cadastrados.procuradores.filter((p) => p.clientesIds.includes(clienteId)).map((p) => p.procuradorId)
    }
    switch (compradorCadastrado.tipo) {
      case 'PessoaFisica':
        return {
          ...base,
          tipoAssinatura: compradorCadastrado.tipoAssinatura ?? null,
          vinculos: compradorCadastrado.vinculos.map((vinculo) => ({
            clienteId: vinculo.clienteId,
            tipo: vinculo.tipo,
            procuradoresIds: procuradoresPorCliente(vinculo.clienteId),
          })),
          procuradoresIds: procuradoresPorCliente(compradorCadastrado.clienteId),
        }
      case 'PessoaJuridica':
        return {
          ...base,
          tipoAssinatura: 'Simples',
          vinculos: compradorCadastrado.vinculos.map((vinculo) => ({
            clienteId: vinculo.clienteId,
            tipo: vinculo.tipo,
            procuradoresIds: procuradoresPorCliente(vinculo.clienteId),
          })),
          procuradoresIds: procuradoresPorCliente(compradorCadastrado.clienteId),
        }
      default:
        throw new Error(`CompradorDtoFactory: tipo de comprador desconhecido → ${JSON.stringify(compradorCadastrado)}`)
    }
  }
}

class ParticipacaoDtoFactory {
  static toDto(
    participacao: TParticipacao,
    formaPagamentoComissao: TFormaPagamentoComissaoEnum,
  ): TPropostaData['participacoes'][number] {
    switch (participacao.corretagem.tipo) {
      case 'percentual':
        return {
          imobiliariaCorretorIdExterno: participacao.imobiliariaCorretor.idExterno,
          percentualCorretagemImobiliaria: participacao.corretagem.percentualCorretagemImobiliaria,
          formaPagamentoComissao,
        }
      case 'valor':
        return {
          imobiliariaCorretorIdExterno: participacao.imobiliariaCorretor.idExterno,
          valorCorretagemImobiliaria: participacao.corretagem.valorCorretagemImobiliaria,
          formaPagamentoComissao,
        }
      default:
        throw new Error(`ParticipacaoDtoFactory: tipo de corretagem desconhecido → ${participacao.corretagem}`)
    }
  }
}

export class PropostaDtoMapper {
  constructor(private readonly data: TPropostaSchemaInput) {}

  public mapear(): TPropostaData {
    return {
      clienteAtendimentoId: this.data.informacoesIniciais.clienteAtendimento.id,
      vendedorId: this.data.informacoesIniciais.vendedor.id,
      termosDaProposta: this.data.detalhesDaProposta.termosDaProposta,
      assinaturaContrato: this.data.detalhesDaProposta.assinaturaContrato as TAssinaturaContratoEnum,
      observacoes: this.data.detalhesDaProposta.observacoes,
      idExternoBloco: this.data.informacoesIniciais.idExternoBloco,
      plantaId: this.data.informacoesIniciais.planta.id || null,
      tipoEmolumento: this.data.detalhesDaProposta.tipoEmolumento ?? null,
      idsExternosVagasPadrao: this.obterInfoVagasPadrao().idsExternosVagasPadrao,
      compradorTitularId: this.obterCompradorTitularId(),
      compradores: this.obterCompradores(),
      participacoes: this.obterParticipacoesImobiliarias(),
      simulacoes: this.obterSimulacoes(),
      opcaoDeVaga: this.obterInfoVagasPadrao().opcaoDeVaga,
    }
  }

  private obterCompradorTitularId(): string {
    const { cadastroCompradores, detalhesDaProposta } = this.data

    if (cadastroCompradores.compradores.length === 1) return cadastroCompradores.compradores[0]!.clienteId
    return detalhesDaProposta.participacaoCompradores.titular.id
  }

  private obterCompradores(): TPropostaData['compradores'] {
    const {
      cadastroCompradores,
      detalhesDaProposta: { participacaoCompradores },
    } = this.data

    const mapComprador = (compradorCadastrado: TPropostaSchemaInput['cadastroCompradores']['compradores'][0]) => {
      const listaPercenturalParticipacao = participacaoCompradores.titular.id
        ? [...participacaoCompradores.compradores, participacaoCompradores.titular]
        : []

      const percentualComprador = listaPercenturalParticipacao.find(
        (compradorDaParticipacao) => compradorDaParticipacao.id === compradorCadastrado.clienteId,
      )?.percentualParticipacao
      return CompradorDtoFactory.toDto(compradorCadastrado, cadastroCompradores, percentualComprador)
    }

    return this.data.cadastroCompradores.compradores.map(mapComprador)
  }

  private obterParticipacoesImobiliarias(): TPropostaData['participacoes'] {
    const { detalhesDaProposta } = this.data
    const { imobiliariasCorretores } = detalhesDaProposta

    if (imobiliariasCorretores.incluirParticipacoes !== 'Sim') return []
    const formaPagamentoComissao = imobiliariasCorretores.formaPagamentoComissao!
    return imobiliariasCorretores.participacoes.map((participacao) =>
      ParticipacaoDtoFactory.toDto(participacao, formaPagamentoComissao),
    )
  }

  private obterSimulacoes(): TPropostaData['simulacoes'] {
    const { informacoesIniciais } = this.data
    const { simulacaoUnidade, simulacaoLote, simulacoesAcessorios, vagaIds } = informacoesIniciais

    const simulacoes = [simulacaoUnidade, simulacaoLote, ...simulacoesAcessorios].filter(
      (simulacao): simulacao is TSimulacaoProposta => !!simulacao,
    )
    const acessoriosIdsParaSubstituicao = new Set(vagaIds.filter((v) => v.substituiVagaPadrao).map((v) => v.id))
    return simulacoes.map((simulacao) => ({
      id: simulacao.id,
      idExternoProduto: simulacao.produto.idExterno,
      substituiVagaPadrao: acessoriosIdsParaSubstituicao.has(simulacao.produto.idExterno),
    }))
  }

  private obterInfoVagasPadrao(): { opcaoDeVaga: TOpcaoVagaEnum; idsExternosVagasPadrao: number[] } {
    const vagaPadraoNaoInformada = this.data.informacoesIniciais.vagaIds.length === 0
    const trocouVagaPadraoPorProdutoDaProposta = this.data.informacoesIniciais.vagaIds.some(
      (vaga) => vaga.substituiVagaPadrao,
    )
    let opcaoDeVaga
    if (trocouVagaPadraoPorProdutoDaProposta) {
      opcaoDeVaga = opcaoVagaEnum.Customizada
    } else if (vagaPadraoNaoInformada) {
      opcaoDeVaga = opcaoVagaEnum.Pendente
    } else {
      opcaoDeVaga = opcaoVagaEnum.Padrao
    }
    return {
      opcaoDeVaga: opcaoDeVaga,
      idsExternosVagasPadrao:
        vagaPadraoNaoInformada || trocouVagaPadraoPorProdutoDaProposta
          ? []
          : this.data.informacoesIniciais.vagaIds.map((v) => v.id),
    }
  }
}
